import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { api, type Lead } from "@/lib/api-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Users,
  Bot,
  BotOff,
  AlertTriangle,
  ArrowRight,
  Phone,
  Clock,
  MessageSquare,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatTimeAgo } from "@/lib/utils";
import { useDashboardRealtime } from "@/lib/useDashboardRealtime";
import { AnalyticsChart } from "@/components/dashboard/AnalyticsChart";

interface Stats {
  totalChats: number;
  aiEnabled: number;
  aiDisabled: number;
  needsAttention: number;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentChats, setRecentChats] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const reloadTimerRef = useRef<number | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const scheduleReload = () => {
    if (reloadTimerRef.current) window.clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = window.setTimeout(() => loadDashboardData(), 300);
  };

  useDashboardRealtime({
    onLeadUpdated: scheduleReload,
    onAlertCreated: scheduleReload,
    onAlertAcked: scheduleReload,
  });

  const loadDashboardData = async () => {
    try {
      const { chats } = await api.getChats({ limit: 100 });
      
      const stats: Stats = {
        totalChats: chats.length,
        aiEnabled: chats.filter(c => c.aiEnabled).length,
        aiDisabled: chats.filter(c => !c.aiEnabled).length,
        needsAttention: chats.filter(c => !c.aiEnabled).length,
      };
      
      setStats(stats);
      setRecentChats(chats.slice(0, 5));
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const statCards = [
    { 
      title: "Total Chats", 
      value: stats?.totalChats ?? 0, 
      icon: MessageSquare, 
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    { 
      title: "AI Active", 
      value: stats?.aiEnabled ?? 0, 
      icon: Bot, 
      color: "text-success",
      bgColor: "bg-success/10"
    },
    { 
      title: "AI Disabled", 
      value: stats?.aiDisabled ?? 0, 
      icon: TrendingUp, 
      color: "text-accent",
      bgColor: "bg-accent/10"
    },
    { 
      title: "Needs Attention", 
      value: stats?.needsAttention ?? 0, 
      icon: AlertTriangle, 
      color: "text-warning",
      bgColor: "bg-warning/10"
    },
  ];

  return (
    <>
      <Header title="Dashboard" subtitle="Overview of your WhatsApp AI agent" />
      
      <div className="p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-border/60 shadow-sm overflow-hidden group hover:border-primary/30 transition-all">
              <CardContent className="p-6">
                {isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
                      <p className="text-3xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Analytics & Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Analytics Chart */}
          <div className="lg:col-span-2">
            <AnalyticsChart />
          </div>

          {/* Quick Actions */}
          <Card className="border-border/60 shadow-sm overflow-hidden flex flex-col row-span-2 rounded-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <div className="w-1.5 h-6 bg-primary rounded-full" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3">
              <Link href="/chats">
                <div className="group flex items-center gap-4 p-4 rounded-2xl bg-primary/5 hover:bg-primary/10 border border-primary/10 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-foreground">View All Chats</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Manage conversations</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </div>
              </Link>

              <Link href="/alerts">
                <div className="group flex items-center gap-4 p-4 rounded-2xl bg-warning/5 hover:bg-warning/10 border border-warning/10 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm text-foreground">Review Alerts</p>
                      {stats && stats.needsAttention > 0 && (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-warning text-[10px] font-bold text-white animate-pulse">
                          {stats.needsAttention}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Action required</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-warning opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </div>
              </Link>

              <Link href="/settings">
                <div className="group flex items-center gap-4 p-4 rounded-2xl bg-accent/5 hover:bg-accent/10 border border-accent/10 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Bot className="w-5 h-5 text-accent-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-foreground">Persona & KB</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Configure AI agent</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </div>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Chats */}
          <Card className="lg:col-span-2 border-border/60 shadow-sm overflow-hidden h-full rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Recent Chats</CardTitle>
              <Link href="/chats">
                <Button variant="ghost" size="sm" className="text-primary">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentChats.length === 0 ? (
                      <EmptyState
                        icon={MessageSquare}
                        title="No chats yet"
                        description="New conversations will appear here"
                        className="py-12"
                      />
              ) : (
                <div className="space-y-4">
                  {recentChats.map((chat) => (
                    <Link key={chat.id} href={`/chats/${chat.id}`}>
                      <div className="flex items-center gap-4 p-4 rounded-2xl hover:bg-muted/50 transition-all cursor-pointer group border border-transparent hover:border-border/60">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                          <span className="text-base font-bold text-primary">
                            {(chat.name || chat.phone)?.[0]?.toUpperCase() || "C"}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-foreground group-hover:text-primary transition-colors truncate">
                              {chat.name || chat.phone}
                            </p>
                            {!chat.aiEnabled && (
                              <Badge variant="secondary" className="text-[10px] bg-warning/10 text-warning border-0 font-bold uppercase tracking-wider h-5 px-1.5 rounded-full">
                                AI Off
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground/70 font-medium">
                            <Phone className="w-3 h-3" />
                            {chat.phone}
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                          <div className="flex items-center gap-1.5 text-muted-foreground/60">
                            <Clock className="w-3 h-3" />
                            <span className="text-[10px] font-bold uppercase tracking-widest">{formatTimeAgo(chat.updatedAt)}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
